/* @flow */

import { compose } from 'ramda';
import { intlShape, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as Yup from 'yup';

import { getLocalizedData } from '@e180/domain/lib/base/l10n/util';
import { getUserFirstName, getUserLastName } from '@e180/domain/lib/user/util';
import { isObject } from '@e180/util/lib/type';

import SignupAboutFormDumb from './SignupAboutFormDumb';
import {
  getSignupI18nFields,
  getSignupExtraFields,
} from 'plugin/signup/util/signupUtils';
import withFeatureDecisions from 'src/shared/app/feature/component/withFeatureDecisions';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { getSelf } from 'src/shared/domain/user/selector/userSelectors';
import { getMembership } from 'src/shared/domain/membership/selector/membershipSelectors';
import {
  getMembershipFlags,
  getMembershipPreferences,
} from '@e180/domain/lib/membership/util';

import type { State } from 'src/shared/core/type/reduxTypes';

type Props = {
  handleSubmit: (Object) => Promise<any>,
  // Self passed
  // $FlowIssue
  intl: intlShape,
  featureDecisions: Object,
};

const mapStateToProps = (
  state: State,
  { handleSubmit, intl, featureDecisions }: Props,
): Object => {
  const event = getEventSafely(state);
  const user = getSelf(state);

  const i18nFieldsMap = {}; // {name: defaultValue}
  const i18nFields = featureDecisions.isSignupEnabled()
    ? getSignupI18nFields(event)
    : null;

  const i18nFieldsSchemas = {};

  if (i18nFields) {
    const localizedData = user ? getLocalizedData(user, intl.locale) : {};
    i18nFields
      // Fields can come directly from the API, so we need these verifications
      .filter((field) => isObject(field))
      .filter(({ name }) => !!name)
      .forEach(({ name }) => {
        i18nFieldsMap[name] = localizedData[name] || '';
        i18nFieldsSchemas[name] = Yup.string().required();
      });
  }

  const extraFieldsMap = {}; // {name: defaultValue}
  const extraFields = featureDecisions.isSignupEnabled()
    ? getSignupExtraFields(event)
    : null;
  const extraFieldsSchemas = {};

  if (extraFields) {
    const membership = getMembership(state);
    const preferences = membership ? getMembershipPreferences(membership) : {};
    const flags = membership ? getMembershipFlags(membership) : {};
    extraFields
      // Fields can come directly from the API, so we need these verifications
      .filter((field) => isObject(field))
      .filter(({ name }) => !!name)
      .forEach(({ name, type }) => {
        if (type === 'checkbox') {
          extraFieldsMap[name] = flags[`signup_${name}`] || false;
          extraFieldsSchemas[name] = Yup.boolean();
        } else {
          extraFieldsMap[name] = preferences[`signup_${name}`] || '';
          extraFieldsSchemas[name] = Yup.string().required();
        }
      });
  }

  const schema = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    ...i18nFieldsSchemas,
    ...extraFieldsSchemas,
  });

  return {
    firstName: user ? getUserFirstName(user) : '',
    lastName: user ? getUserLastName(user) : '',
    i18nFields: i18nFieldsMap,
    extraFields: extraFieldsMap,
    schema,
    handleSubmit,
  };
};

export default compose(
  injectIntl,
  withFeatureDecisions,
  connect(mapStateToProps),
)(SignupAboutFormDumb);
